import { useState } from 'react';
import { useParams } from 'react-router';

import {
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  Box
} from '@mui/material';

import { useQueryClient } from '@tanstack/react-query';
import { makeStyles } from 'tss-react/mui';

import Button from '@/components/button';
import Dialog from '@/components/dialog';
import Loading from '@/components/loading';
import {
  DEFAULT_ERRORS,
  DEFAULT_PREVIEW_RESULTS
} from '@/constants/evaluation';
import {
  useCreateEvaluation,
  useEvaluationTitleExists,
  useFetchEvaluation,
  usePreviewEvaluation,
  useUpdateEvaluation
} from '@/hooks/queries/evaluations';
import useAlert from '@/hooks/useAlert';
import useWorkspaceNavigate from '@/hooks/useWorkspaceNavigate';
import { Evaluation } from '@/models/evaluations';
import {
  convertParametersToForm,
  convertFormToParameters
} from '@/utils/evaluations/evaluation';
import {
  hasEmptyLogicalLevel,
  platformsEmpty
} from '@/utils/evaluations/validation';
import getRoute from '@/utils/getRoute';
import { validateString } from '@/utils/text';

import QueryDrawer from '../create/_partials/QueryDrawer';
import Step1 from '../create/_partials/Step1';
import Step2 from '../create/_partials/Step2';
import Step3 from '../create/_partials/Step3';
import Step4 from '../create/_partials/Step4';

const useStyles = makeStyles()(() => ({
  page: {
    padding: '24px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  stepper: {
    height: '72px'
  }
}));

type DataDraft = {
  title: string;
  parameters: Evaluation.ParametersToPost;
};

type FormState = ReturnType<typeof convertParametersToForm> & { title: string };

function EditEvaluation() {
  const evaluationId = Number(useParams().evaluationId);
  const {
    data: evaluation,
    isLoading,
    isError
  } = useFetchEvaluation({ id: evaluationId });

  if (isLoading) return <Loading />;
  if (isError || !evaluation) return <div>Error loading evaluation data</div>;

  return (
    <EditEvaluationForm evaluationId={evaluationId} evaluation={evaluation} />
  );
}

interface EditEvaluationFormProps {
  evaluationId: number;
  evaluation: Evaluation.Details;
}

function EditEvaluationForm({
  evaluationId,
  evaluation
}: EditEvaluationFormProps) {
  const { classes } = useStyles();
  const queryClient = useQueryClient();
  const navigate = useWorkspaceNavigate();
  const alert = useAlert();

  const { parameters, title: initialTitle, requestedServices } = evaluation;

  const initialDateMode = parameters?.endCreationDate === null;

  const goToDetail = () => navigate(getRoute.evaluations.DETAIL(evaluationId));

  const [activeStep, setActiveStep] = useState(0);
  const [isExitDialogOpen, setExitDialogOpen] = useState(false);
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [dataDraft, setDataDraft] = useState<DataDraft | null>(null);
  const [viewQueryOpen, setViewQueryOpen] = useState(false);

  const [form, setForm] = useState({
    ...convertParametersToForm(parameters),
    title: initialTitle
  });
  const [errors, setErrors] = useState<Evaluation.FormErrors>(DEFAULT_ERRORS);

  const handleTitleSave = `${dataDraft?.title} (copy)`;

  const { mutate: updateEvaluation, isPending: isUpdateSubmitting } =
    useUpdateEvaluation(
      evaluationId,
      () => {
        queryClient.invalidateQueries({
          queryKey: ['evaluation', evaluationId]
        });
        queryClient.invalidateQueries({
          queryKey: ['evaluations'],
          refetchType: 'inactive'
        });
        queryClient.refetchQueries({ queryKey: ['posts'] });
        queryClient.invalidateQueries({
          queryKey: ['post'],
          refetchType: 'inactive'
        });
        queryClient.invalidateQueries({
          queryKey: ['creatorsAutocomplete'],
          refetchType: 'inactive'
        });
        alert.success('The evaluation criteria has been processed.');
        goToDetail();
      },
      () =>
        alert.error(
          'Oops, something went wrong. The evaluation criteria could not be edited.'
        )
    );

  const { mutate: createEvaluation, isPending: isCreateSubmitting } =
    useCreateEvaluation(
      (res) => {
        const { data } = res ?? {};
        if (!data) return;
        const { id } = data;

        queryClient.invalidateQueries({
          queryKey: ['evaluations'],
          refetchType: 'inactive'
        });

        alert.success(
          `Evaluation "${handleTitleSave}" has been processed.`,
          <Button
            variant="text"
            size="small"
            onClick={() => navigate(getRoute.evaluations.DETAIL(id))}
            text="open"
          />
        );

        navigate(getRoute.evaluations.DETAIL(id));
      },
      () =>
        alert.error(
          `Oops, something went wrong. Evaluation "${handleTitleSave}" could not be processed.`
        )
    );

  const {
    data: previewData,
    isFetching: isLoadingPreviewResults,
    isError: errorPreview
  } = usePreviewEvaluation(convertFormToParameters(form), activeStep === 3);

  const previewResults = previewData ?? DEFAULT_PREVIEW_RESULTS;

  const { mutate: evaluationTitleExists } = useEvaluationTitleExists((res) => {
    const { data } = res ?? {};
    const titleAlreadyExists = Object.keys(data).length > 0;
    if (titleAlreadyExists)
      setErrors({ ...errors, title: 'This title already exits' });
    else setActiveStep(1);
  });

  const handleClickPrevious = () => {
    if (activeStep === 3) {
      queryClient.cancelQueries({ queryKey: ['previewEvaluation'] });
      queryClient.removeQueries({ queryKey: ['previewEvaluation'] });
    }
    setActiveStep((prev) => prev - 1);
  };

  const isSubmitting = isUpdateSubmitting || isCreateSubmitting;

  const handleBackClick =
    activeStep === 0 ? () => setExitDialogOpen(true) : handleClickPrevious;

  const handleClickContinue = () => {
    switch (activeStep) {
      case 0: {
        const { title } = form;
        if (!validateString(title, undefined, 3)) {
          setErrors({
            ...errors,
            title: 'Please create a title of 3 characters or more'
          });
          return;
        }
        if (platformsEmpty(form.platforms)) {
          setErrors({
            ...errors,
            platforms: 'At least one platform should be selected'
          });
          return;
        }
        if (initialTitle !== title) evaluationTitleExists(title);
        else setActiveStep(activeStep + 1);
        break;
      }
      case 1:
        setActiveStep(activeStep + 1);
        break;
      case 2:
        setActiveStep(activeStep + 1);
        break;
      case 3: {
        const data = {
          title: form.title,
          parameters: convertFormToParameters(form)
        };
        setDataDraft(data);
        setIsSaveDialogOpen(true);
        break;
      }
      default:
        break;
    }
  };

  const handleOverwrite = () => {
    setIsSaveDialogOpen(false);
    if (dataDraft) updateEvaluation(dataDraft);
  };

  const handleSaveAsCopy = () => {
    setIsSaveDialogOpen(false);
    if (dataDraft) createEvaluation({ ...dataDraft, title: handleTitleSave });
  };

  const handleStep1Change = (field: keyof Evaluation.Form, value: unknown) => {
    const newForm = { ...form, [field]: value };
    const newErrors: Evaluation.FormErrors = {
      title: field === 'title' ? '' : errors.title,
      followersRange: '',
      platforms: ''
    };

    const {
      followersRange: { min, max }
    } = newForm;
    if (max !== '' && Number(min) > Number(max)) {
      newErrors.followersRange = 'Minimum value cannot be lower than maximum';
    }

    if (platformsEmpty(newForm.platforms)) {
      newErrors.platforms = 'At least one platform should be selected';
    }

    setForm(newForm);
    setErrors(newErrors);
  };

  const steps = [
    {
      title: 'Add Title',
      content: (
        <Step1
          errors={errors}
          onChange={handleStep1Change}
          values={form}
          onClickContinue={handleClickContinue}
          initialDateMode={initialDateMode}
        />
      )
    },
    {
      title: 'Extra details',
      content: (
        <Step2
          onChange={(field, value) =>
            setForm((prev) => ({ ...prev, [field]: value }))
          }
          showSubtitle={false}
          values={form}
        />
      )
    },
    {
      title: 'Define Keywords',
      content: (
        <Step3
          onChange={(
            field: keyof FormState,
            value: FormState[keyof FormState]
          ) => {
            setForm({ ...form, [field]: value });
          }}
          showSubtitle={false}
          values={{
            keywordQuery: form.keywordQuery,
            highlightedKeywords: form.highlightedKeywords
          }}
        />
      )
    },
    {
      title: 'Review Evaluation',
      content: (
        <Step4
          isDisabled={true} // VR shouldn't be edited
          results={previewResults}
          requestedServices={requestedServices}
          isLoading={isLoadingPreviewResults}
          error={errorPreview}
        />
      )
    }
  ];

  const isContinueDisabled =
    (activeStep === 0 &&
      (!!errors.title || !!errors.followersRange || !!errors.platforms)) ||
    (activeStep === 2 && hasEmptyLogicalLevel(form.keywordQuery)) ||
    (activeStep === 3 && isLoadingPreviewResults);

  return (
    <>
      <Box className={classes.page}>
        <Typography variant="h5">Edit evaluation</Typography>

        <Stepper className={classes.stepper} activeStep={activeStep}>
          {steps.map((step) => (
            <Step key={step.title} sx={{ p: 0 }}>
              <StepLabel>{step.title}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box flex={1} overflow="auto">
          {steps[activeStep].content}
        </Box>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>
            {activeStep === 2 && (
              <Button
                id="evaluation-view-query"
                variant="outlined"
                text="view query"
                onClick={() => setViewQueryOpen(true)}
              />
            )}
          </Box>

          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button
              variant="text"
              text="back"
              onClick={handleBackClick}
              disabled={isSubmitting}
            />
            <Button
              disabled={isContinueDisabled}
              onClick={handleClickContinue}
              text={activeStep === 3 ? 'save changes' : 'continue'}
              variant="contained"
              loading={isSubmitting}
            />
          </Stack>
        </Stack>
      </Box>

      <Dialog
        open={isExitDialogOpen}
        title="Leaving this page"
        dialogContent={
          <Typography>
            Are you sure you want to leave this page? Your changes will not be
            saved.
          </Typography>
        }
        actions={
          <>
            <Button
              onClick={() => setExitDialogOpen(false)}
              variant="text"
              text="cancel"
            />
            <Button id="evaluation-leave" onClick={goToDetail} text="leave" />
          </>
        }
      />
      <Dialog
        open={isSaveDialogOpen}
        title="Save changes"
        dialogContent={
          <Typography>
            Saving these changes may affect which posts appear in this
            evaluation. Posts that you have rated earlier may no longer appear
            in the changed evaluation, and their ratings will be lost. Are you
            sure you want to continue? You can also save the changed evaluation
            as a copy instead.
          </Typography>
        }
        actions={
          <>
            <Button
              onClick={() => setIsSaveDialogOpen(false)}
              variant="text"
              text="cancel"
            />
            <Button
              onClick={handleSaveAsCopy}
              variant="outlined"
              text="save as copy"
            />
            <Button
              onClick={handleOverwrite}
              text="overwrite"
              loading={isUpdateSubmitting}
            />
          </>
        }
      />
      <QueryDrawer
        open={viewQueryOpen}
        onClose={() => setViewQueryOpen(false)}
        query={form.keywordQuery}
      />
    </>
  );
}

export default EditEvaluation;
